import {
  Settings,
  Smartphone,
  CreditCard,
  Webhook,
  PanelTop,
  LucideProps,
} from "lucide-react";
import type { ForwardRefExoticComponent, RefAttributes } from "react";
import type { PlanFeatures } from "@/lib/utils/planFeatures";

export type PlanFeatureFlag = {
  [K in keyof PlanFeatures]: PlanFeatures[K] extends boolean ? K : never;
}[keyof PlanFeatures];

export type SidebarNavItem = {
  name: string;
  link: NAVIGATION_PATHS;
  icon?: ForwardRefExoticComponent<
    Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
  >;
  disabled?: boolean;
  locked?: boolean;
  beta?: boolean;
  crossOrigin?: boolean;
  requiredEntitlement?: PlanFeatureFlag;
};

export enum NAVIGATION_PATHS {
  Signup = "/auth/signup",
  Signin = "/auth/signin",
  CreateOrganization = "/create-organization", // used also in protected group when creating new organization
  ConfirmEmail = "/auth/confirm-email",
  UpdatePassword = "/auth/update-password",
  ResetPassword = "/auth/reset-password",
  AuthCodeError = "/auth/auth-code-error",
  AcceptInvite = "/auth/accept-invite",
  TermsAndConditions = "/terms-and-conditions",
  PrivacyPolicy = "/privacy-policy",
  RefundTerms = "/refund-terms",

  // ------- dashboard
  Dashboard = "/",
  AnalyticsOverview = "/analytics/overview",
  AnalyticsLinks = "/analytics/links",
  AnalyticsEvents = "/analytics/app-events",
  AnalyticsImports = "/analytics/imports",
  Applications = "/applications",
  Billing = "/billing",
  BillingPlans = "/billing/plans",
  Pricing = "/pricing",
  Webhooks = "/webhooks",
  SmartBanners = "/smart-banners",

  // ------- settings
  OrganizationsSettings = "/settings/organizations",
  AccountSettings = "/settings/account", // used both under subdomain and main domain
}

export const NAVIGATION_HEADER_TITLES: Partial<
  Record<NAVIGATION_PATHS, string>
> = {
  [NAVIGATION_PATHS.Dashboard]: "Apps",
  [NAVIGATION_PATHS.AccountSettings]: "Account Settings",
  [NAVIGATION_PATHS.OrganizationsSettings]: "Organization Settings",
  [NAVIGATION_PATHS.Applications]: "Apps",
  [NAVIGATION_PATHS.AnalyticsOverview]: "Analytics",
  [NAVIGATION_PATHS.AnalyticsLinks]: "Analytics",
  [NAVIGATION_PATHS.AnalyticsEvents]: "Analytics",
  [NAVIGATION_PATHS.AnalyticsImports]: "Analytics",
  [NAVIGATION_PATHS.Billing]: "Billing",
  [NAVIGATION_PATHS.BillingPlans]: "Billing",
  [NAVIGATION_PATHS.Pricing]: "Pricing",
  [NAVIGATION_PATHS.SmartBanners]: "Smart banners",
};

export const SIDEBAR_APP_NAVIGATION: SidebarNavItem[] = [
  { name: "Apps", icon: Smartphone, link: NAVIGATION_PATHS.Dashboard },
];
export const SIDEBAR_ANALYTICS_NAVIGATION: SidebarNavItem[] = [
  { name: "Overview", link: NAVIGATION_PATHS.AnalyticsOverview },
  {
    name: "Links",
    link: NAVIGATION_PATHS.AnalyticsLinks,
    requiredEntitlement: "canAccessAnalyticsLinks",
  },
  {
    name: "Events",
    link: NAVIGATION_PATHS.AnalyticsEvents,
    requiredEntitlement: "canAccessAnalyticsEvents",
  },
  { name: "Imports", link: NAVIGATION_PATHS.AnalyticsImports, beta: true },
];
export const SIDEBAR_ORGANIZATION_NAVIGATION: SidebarNavItem[] = [
  {
    name: "Settings",
    icon: Settings,
    link: NAVIGATION_PATHS.OrganizationsSettings,
  },
  {
    name: "Billing",
    icon: CreditCard,
    link: NAVIGATION_PATHS.Billing,
    crossOrigin: true,
  },
  {
    name: "Smart banners",
    icon: PanelTop,
    link: NAVIGATION_PATHS.SmartBanners,
    beta: true,
  },
  {
    name: "Webhooks",
    icon: Webhook,
    link: NAVIGATION_PATHS.Webhooks,
  },
];
